import type { SuccessResponse } from "@shared/types";
import apiClient from "@/app/api/client";
import { endpoints } from "@/shared/api/endpoints";

export type PermissionItem = {
	resource: string;
	action: string;
};

export type AdminRole = {
	id: string;
	name: string;
	description?: string;
	permissions: PermissionItem[];
	inherits?: string[];
	scope?: string;
	metadata?: Record<string, any>;
};

export type PermissionsResource = {
	name: string;
	actions: string[];
};

export type PermissionsListResponse = {
	resources: PermissionsResource[];
};

export const PERMISSION_ACTION_ORDER = ["create", "read", "update", "delete"] as const;

const RESOURCE_LABELS: Record<string, string> = {
	"*": "Barcha resurslar",
	users: "Foydalanuvchilar",
	roles: "Rollar va Huquqlar",
	buildings: "Binolar",
	blocks: "Bloklar",
	sections: "Seksiyalar",
	rooms: "Xonalar",
	clients: "Mijozlar",
	leads: "Lidlar",
	sales: "Sotuvlar",
	payments: "To'lovlar",
	reports: "Hisobotlar",
	sms: "SMS",
};

const ACTION_LABELS: Record<string, string> = {
	"*": "Barchasi",
	create: "Yaratish",
	read: "Ko'rish",
	update: "Tahrirlash",
	delete: "O'chirish",
};

export function getResourceLabel(resource: string) {
	return RESOURCE_LABELS[resource.toLowerCase()] || resource;
}

export function getActionLabel(action: string) {
	return ACTION_LABELS[action.toLowerCase()] || action;
}

export function getPermissionActionColumns(resources: PermissionsResource[]) {
	const availableActions = new Set(resources.flatMap((resource) => resource.actions));
	return PERMISSION_ACTION_ORDER.filter((action) => availableActions.has(action));
}

function parsePermissions(perms: any): PermissionItem[] {
	if (!perms) {
		return [];
	}
	if (Array.isArray(perms)) {
		return perms;
	}
	if (typeof perms === "string") {
		try {
			const parsed = JSON.parse(perms);
			return Array.isArray(parsed) ? parsed : [];
		} catch {
			return [];
		}
	}
	return [];
}

function parseInherits(inherits: any): string[] {
	if (!inherits) {
		return [];
	}
	if (Array.isArray(inherits)) {
		return inherits;
	}
	if (typeof inherits === "string") {
		try {
			const parsed = JSON.parse(inherits);
			return Array.isArray(parsed) ? parsed : [inherits];
		} catch {
			return [inherits];
		}
	}
	return [];
}

function mapBackendRole(data: any): AdminRole {
	return {
		id: data.id,
		name: data.name,
		description: data.description,
		permissions: parsePermissions(data.permissions),
		inherits: parseInherits(data.inherits),
		scope: data.scope,
		metadata:
			typeof data.metadata === "string"
				? (() => {
						try {
							return JSON.parse(data.metadata);
						} catch {
							return {};
						}
					})()
				: data.metadata || {},
	};
}

export async function getRoles(params?: { search?: string }) {
	const response = await apiClient.get<any>(endpoints.roles.list, { params });
	const items = (response.data.data?.items || response.data.data || []).map(mapBackendRole);
	const total = response.data.data?.meta?.total ?? items.length;
	const limit = response.data.data?.meta?.limit ?? 20;
	const page = response.data.data?.meta?.page ?? 1;
	const totalPages = response.data.data?.meta?.totalPages ?? 1;

	return {
		items,
		meta: {
			total,
			page,
			limit,
			totalPages,
		},
	};
}

export async function getPermissions() {
	const response = await apiClient.get<SuccessResponse<PermissionsListResponse>>(
		endpoints.roles.permissions
	);
	return response.data.data.resources || [];
}

export async function getRoleById(roleId: string) {
	const response = await apiClient.get<SuccessResponse<any>>(endpoints.roles.detail(roleId));
	return mapBackendRole(response.data.data);
}

export async function submitRoleCreate(payload: Omit<AdminRole, "scope">) {
	const response = await apiClient.post<SuccessResponse<any>>(endpoints.roles.create, payload);
	return mapBackendRole(response.data.data);
}

export async function submitRoleUpdate(
	roleId: string,
	payload: Partial<Omit<AdminRole, "id" | "scope">>
) {
	const response = await apiClient.patch<SuccessResponse<any>>(
		endpoints.roles.update(roleId),
		payload
	);
	return mapBackendRole(response.data.data);
}

export async function submitRoleDelete(roleId: string) {
	await apiClient.delete<void>(endpoints.roles.delete(roleId));
}
